import { am5, am5xy } from '@local/finsera-core/src/libs';

export const createLineOverlay = ({
  root,
  chart,
  xAxis,
  yAxis,
  data,
  xAxisField,
  yAxisField,
  lineOverlay,
}: {
  root: am5.Root;
  chart: am5xy.XYChart;
  xAxis: am5xy.ValueAxis<am5xy.AxisRenderer>;
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>;
  data: any[];
  xAxisField: string;
  yAxisField: string;
  lineOverlay: 'trendline' | 'x=y' | 'fit';
}) => {
  if (!lineOverlay) return;

  const allData = data.flatMap((item) => ({
    x: item[xAxisField],
    y: item[yAxisField],
  }));

  if (lineOverlay === 'trendline') {
    // Calculate trendline using linear regression
    const n = allData.length;
    const sumX = allData.reduce((sum, point) => sum + point.x, 0);
    const sumY = allData.reduce((sum, point) => sum + point.y, 0);
    const sumXY = allData.reduce((sum, point) => sum + point.x * point.y, 0);
    const sumXX = allData.reduce((sum, point) => sum + point.x * point.x, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Create trendline series
    const trendlineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Trendline',
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: 'x',
        valueYField: 'y',
        stroke: am5.color('rgb(34, 160, 70)'),
        baseAxis: xAxis,
        legendLabelText: '',
        legendValueText: '',
        ignoreMinMax: true,
      })
    );

    trendlineSeries.strokes.template.setAll({
      strokeWidth: 2,
      strokeDasharray: [5, 5],
    });

    // Calculate trendline points using the full chart range
    const minValue = Math.min(
      Math.min(...allData.map((point) => point.x)),
      Math.min(...allData.map((point) => point.y))
    );
    const maxValue = Math.max(
      Math.max(...allData.map((point) => point.x)),
      Math.max(...allData.map((point) => point.y))
    );

    const trendlineData = [
      { x: minValue, y: slope * minValue + intercept },
      { x: maxValue, y: slope * maxValue + intercept },
    ];

    trendlineSeries.data.setAll(trendlineData);
  } else if (lineOverlay === 'x=y') {
    // Create reference line at x=y
    const referenceSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Reference Line',
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: 'x',
        valueYField: 'y',
        stroke: am5.color('rgb(34, 160, 70)'),
        baseAxis: xAxis,
        legendLabelText: 'Reference Line',
        legendValueText: '',
        ignoreMinMax: true,
      })
    );

    referenceSeries.strokes.template.setAll({
      strokeWidth: 2,
      strokeDasharray: [5, 5],
    });

    const referenceData = [
      { x: -10, y: -10 },
      { x: 10, y: 10 },
    ];

    referenceSeries.data.setAll(referenceData);
  } else if (lineOverlay === 'fit') {
    const fitSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: xAxisField,
        valueYField: 'fit',
        stroke: am5.color('rgb(34, 160, 70)'),
        baseAxis: xAxis,
      })
    );

    fitSeries.strokes.template.setAll({
      strokeWidth: 2,
      strokeOpacity: 0.7,
    });

    fitSeries.data.setAll(data);
  }
};
